import type { ReadonlyJSONValue } from "@rocicorp/zero";
import * as Sentry from "@sentry/react";
import { keyframes, styled } from "goober";
import { useToolOutput } from "../../hooks/useToolOutput";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { Noodle } from "../Noodle";
import { Text } from "../Text";
import type {
  ChatTool,
  ToolResult,
  ToolUse as ToolUseData,
  ToolUseProps,
} from "./ChatTool";
import {
  ExpandedContent,
  ExpandedContentHover,
  ExpandedTimestamp,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";
import { ToolStatus } from "./ToolStatus";

const fadeInSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 2px;
  align-items: stretch;
  margin-left: 12px;
  max-width: 100%;
  overflow: hidden;
  animation: ${fadeInSlideUp} 0.075s ease-out;
`;

const Inner = styled("div")`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const NoodleColumn = styled("div")`
  width: 12px;
  align-self: stretch;
  display: flex;
`;

// Stays one element as `expandable` flips mid-stream, since swapping the type
// would remount the header; disabled when dead, un-dimmed because it's a label.
const HeaderWrapper = styled(ClickButton)`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
  border-radius: 9999px;
  overflow: hidden;
  width: fit-content;
  max-width: 100%;
  text-align: left;
  &:disabled {
    opacity: 1;
  }
  /* goober compiles &:not(:disabled):hover into a broken descendant selector, so :hover must come first */
  &:hover:not(:disabled) {
    background-color: var(--alpha4);
  }
`;

const HeaderInner = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;

  & > *:not(:first-child) {
    display: block;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

function FallbackHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const done = !!result && !result.isError;
  const input = toolUse.input as Record<string, unknown> | null;
  const text = done
    ? ((input?.completedDescription as string) ??
      `Completed ${toolUse.toolName}`)
    : ((input?.activeDescription as string) ?? `Using ${toolUse.toolName}…`);
  return (
    <ToolStatus icon="tool-case" text={text} pending={!done && !interrupted} />
  );
}

function FallbackBody({ toolUse, result }: ToolUseProps) {
  return (
    <Text variant="code-small" color="var(--text-secondary)">
      <pre
        style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {JSON.stringify(toolUse.input, null, 2)}
      </pre>
      {result && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {JSON.stringify(result.output, null, 2)}
        </pre>
      )}
    </Text>
  );
}

type Props = {
  toolUse: ToolUseData;
  result: ToolResult | null;
  toolAfter: boolean;
  expanded: boolean;
  toggleExpanded: () => void;
  tool: ChatTool | undefined;
  createdAt: number;
  completedAt?: number | null;
  interrupted?: boolean;
  onProvideResult?: (
    entryId: string,
    output: unknown,
    isError: boolean
  ) => void;
};

export function ToolUse({
  toolUse,
  result,
  toolAfter,
  expanded,
  toggleExpanded,
  tool,
  createdAt,
  completedAt,
  interrupted,
  onProvideResult,
}: Props) {
  const shouldLoad = !!(
    expanded ||
    tool?.alwaysExpand ||
    (tool?.alwaysLoadOutput && result)
  );

  const { output: lazyOutput } = useToolOutput(toolUse.id, shouldLoad);

  const effectiveResult: ToolResult | null = result
    ? {
        ...result,
        output: (lazyOutput as ReadonlyJSONValue) ?? result.output,
      }
    : null;

  const Header = tool?.Header ?? FallbackHeader;
  const Body = tool?.Body ?? FallbackBody;
  const data = tool?.data;
  const activate = tool?.onActivate;
  const hasContent = result !== null || tool?.alwaysExpand || !tool;

  const expandable = hasContent;
  const isExpanded = expanded || tool?.alwaysExpand;

  const headerInner = (
    <HeaderInner>
      <Header
        toolUse={toolUse}
        result={effectiveResult}
        createdAt={createdAt}
        data={data}
        interrupted={!!interrupted}
      />
    </HeaderInner>
  );

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <Container>
          <NoodleColumn>
            <Noodle gridSize={7} variant={toolAfter ? "branch" : "terminate"} />
          </NoodleColumn>
          <Inner>
            <HeaderWrapper disabled>
              <HeaderInner>
                <Icon name="circle-minus" size={12} color="var(--red-600)" />
                <Text
                  variant="text-small"
                  color="var(--text-secondary)"
                  ellipsis
                >
                  Failed to render {toolUse.toolName}
                </Text>
              </HeaderInner>
            </HeaderWrapper>
            <Text
              variant="code-small"
              color="var(--text-tertiary)"
              style={{ padding: "2px 6px 8px 6px" }}
            >
              {error instanceof Error ? error.message : String(error)}
            </Text>
          </Inner>
        </Container>
      )}
    >
      <Container>
        <NoodleColumn>
          <Noodle gridSize={7} variant={toolAfter ? "branch" : "terminate"} />
        </NoodleColumn>
        <Inner>
          <HeaderWrapper
            disabled={!expandable}
            onClick={
              activate
                ? () =>
                    activate({
                      toolUse,
                      result: effectiveResult,
                      createdAt,
                      data,
                      interrupted: !!interrupted,
                      onProvideResult,
                    })
                : toggleExpanded
            }
          >
            {headerInner}
          </HeaderWrapper>

          {!activate && (
            <ExpandWrapper data-expanded={isExpanded}>
              <ExpandInner data-expanded={isExpanded}>
                <ExpandedContentHover>
                  <ExpandedContent>
                    <Body
                      toolUse={toolUse}
                      result={effectiveResult}
                      createdAt={createdAt}
                      data={data}
                      interrupted={!!interrupted}
                      onProvideResult={onProvideResult}
                    />
                  </ExpandedContent>
                  <ExpandedTimestamp
                    createdAt={createdAt}
                    durationMs={
                      completedAt != null ? completedAt - createdAt : null
                    }
                  />
                </ExpandedContentHover>
              </ExpandInner>
            </ExpandWrapper>
          )}
        </Inner>
      </Container>
    </Sentry.ErrorBoundary>
  );
}
